import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { type MinimalRouterStateSnapshot, ROUTER_NAVIGATION, type RouterNavigationAction } from "@ngrx/router-store";
import { PART_REPOSITORY } from "@partikelart/parts/data-access-core";
import { EMPTY_PART, EMPTY_PART_ID } from "@partikelart/parts/domain";
import { SurfaceReportActions } from "@partikelart/surface/state";
import { catchError, concatMap, filter, map, of, switchMap, tap } from "rxjs";
import { PartActions } from "./part.actions";

@Injectable()
export class PartEffects {
	private readonly actions$ = inject(Actions);
	private readonly partData = inject(PART_REPOSITORY);
	private readonly router = inject(Router);
	loadPartFromRoute$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ROUTER_NAVIGATION),
			map((action: RouterNavigationAction<MinimalRouterStateSnapshot>) => {
				// Traverse the router state tree to get the deepest active route (the leaf route)
				// WILL BE REMOVED WHEN THE CUSTOM SERIALISER IS IMPLEMENTED
				let route = action.payload.routerState.root;
				while (route && route.firstChild) {
					route = route.firstChild;
				}
				return route ? route.params : {};
			}),
			filter((params) => !!params["no"] && params["no"] !== "new" && params["no"] !== "list"),
			map((params) => PartActions.loadPartByNumber({ partNumber: params["no"] })),
		),
	);

	loadReportsOnPartChange$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PartActions.loadPartByNumberSuccess, PartActions.loadPartByIdSuccess),
			map((action) =>
				SurfaceReportActions.loadReports({
					params: { partId: action.part.id },
				}),
			),
		),
	);

	loadParts$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PartActions.loadParts),
			switchMap(({ siteId }) =>
				this.partData.getAll(siteId).pipe(
					map((parts) => PartActions.loadPartsSuccess({ parts })),
					catchError((error) => of(PartActions.loadPartsFailure({ error: error.message }))),
				),
			),
		),
	);

	loadPartById$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PartActions.loadPartById),
			switchMap(({ id }) => {
				if (id === EMPTY_PART_ID) return of(PartActions.loadPartByIdSuccess({ part: EMPTY_PART }));
				return this.partData.getById(id).pipe(
					map((part) => {
						if (!part) throw new Error("Part not found");
						return PartActions.loadPartByIdSuccess({ part });
					}),
					catchError((error) => of(PartActions.loadPartByIdFailure({ error: error.message }))),
				);
			}),
		),
	);

	loadPartByNumber$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PartActions.loadPartByNumber),
			switchMap(({ partNumber }) =>
				this.partData.getByPartNumber(partNumber).pipe(
					map((part) => {
						if (!part) throw new Error("Part not found");
						return PartActions.loadPartByNumberSuccess({ part });
					}),
					catchError((error) => of(PartActions.loadPartByNumberFailure({ error: error.message }))),
				),
			),
		),
	);

	createPart$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PartActions.createPart),
			concatMap(({ input }) =>
				this.partData.create(input).pipe(
					map((part) => PartActions.createPartSuccess({ part })),
					catchError((error) => of(PartActions.createPartFailure({ error: error.message }))),
				),
			),
		),
	);

	updatePart$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PartActions.updatePart),
			concatMap(({ id, changes }) =>
				this.partData.update(id, changes).pipe(
					map((part) => PartActions.updatePartSuccess({ part })),
					catchError((error) => of(PartActions.updatePartFailure({ error: error.message }))),
				),
			),
		),
	);

	deletePart$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PartActions.deletePart),
			concatMap(({ id }) =>
				this.partData.delete(id).pipe(
					map(() => PartActions.deletePartSuccess({ id })),
					catchError((error) => of(PartActions.deletePartFailure({ error: error.message }))),
				),
			),
		),
	);

	navigateAfterCreatePart$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(PartActions.createPartSuccess),
				tap(() => {
					this.router.navigate(["/home/parts/list"]);
				}),
			),
		{ dispatch: false },
	);
}
